"use client";

import {
  Award,
  BadgeDollarSign,
  BookOpen,
  CalendarDays,
  FileText,
  FlaskConical,
  GraduationCap,
  Lightbulb,
  type LucideIcon,
} from "lucide-react";

import { normalizeBlogSearchText } from "@/lib/blog/search-lexical";

export type SearchSuggestionIconKind =
  | "notes"
  | "tuition"
  | "practical"
  | "scholarship"
  | "bursary"
  | "calendar"
  | "tips"
  | "default";

const ICON_BY_KIND: Record<SearchSuggestionIconKind, LucideIcon> = {
  notes: BookOpen,
  tuition: GraduationCap,
  practical: FlaskConical,
  scholarship: Award,
  bursary: BadgeDollarSign,
  calendar: CalendarDays,
  tips: Lightbulb,
  default: FileText,
};

function hasAnyToken(text: string, tokens: readonly string[]): boolean {
  return tokens.some((token) => text.includes(token));
}

export function classifySearchSuggestionIcon(
  suggestion: string
): SearchSuggestionIconKind {
  const normalized = normalizeBlogSearchText(suggestion);

  if (hasAnyToken(normalized, ["bursary", "bursaries"])) return "bursary";
  if (hasAnyToken(normalized, ["scholarship", "scholarships"])) {
    return "scholarship";
  }
  if (hasAnyToken(normalized, ["tuition", "tutor", "programme"])) {
    return "tuition";
  }
  if (
    hasAnyToken(normalized, [
      "practical",
      "practicals",
      "experiment",
      "experiments",
      "titration",
      "lab",
    ])
  ) {
    return "practical";
  }
  if (
    hasAnyToken(normalized, [
      "calendar",
      "date",
      "dates",
      "holiday",
      "holidays",
      "schedule",
      "exam timetable",
    ])
  ) {
    return "calendar";
  }
  if (
    hasAnyToken(normalized, [
      "notes",
      "syllabus",
      "definition",
      "definitions",
      "formula",
      "formulae",
      "guide",
    ])
  ) {
    return "notes";
  }
  if (
    hasAnyToken(normalized, [
      "study tip",
      "study tips",
      "strategy",
      "revision",
      "interview prep",
    ])
  ) {
    return "tips";
  }

  return "default";
}

export function SearchSuggestionIcon({
  suggestion,
  isActive,
}: {
  suggestion: string;
  isActive: boolean;
}) {
  const Icon = ICON_BY_KIND[classifySearchSuggestionIcon(suggestion)];

  return (
    <span
      aria-hidden="true"
      className={`mr-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${
        isActive
          ? "border-white/40 bg-white/15 text-white"
          : "border-blue-100 bg-blue-50 text-blue-700"
      }`}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
    </span>
  );
}
